import React, { useEffect, useRef } from "react";

const NUM_CONFETTI = 200;
const COLORS = [
  [85, 71, 106],
  [174, 61, 99],
  [219, 56, 83],
  [244, 92, 68],
  [248, 182, 70],
];
const PI_2 = Math.PI * 2;

const ConfettiCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const confettiRef = useRef<any[]>([]);
  const xposRef = useRef(0.5);

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    class Confetti {
      style: number[];
      rgb: string;
      w: number;
      h: number;
      x: number;
      y: number;
      vx: number;
      vy: number;
      opacity: number;
      dop: number;
      xmax: number;
      ymax: number;
      rotation: number;
      rotationSpeed: number;

      constructor() {
        this.style = COLORS[~~range(0, COLORS.length)];
        this.rgb = `rgba(${this.style[0]},${this.style[1]},${this.style[2]}`;
        this.w = ~~range(6, 24); // width of rectangle
        this.h = ~~range(8, 16); // height of rectangle
        this.rotation = range(0, PI_2);
        this.rotationSpeed = range(-0.05, 0.05);
        this.replace();
      }

      replace() {
        const canvas = canvasRef.current!;
        this.opacity = 0;
        this.dop = 0.03 * range(1, 4);
        this.x = range(-this.w, canvas.width - this.w);
        this.y = range(-20, canvas.height - this.h);
        this.xmax = canvas.width - this.w;
        this.ymax = canvas.height - this.h;
        this.vx = range(0, 2) + 8 * xposRef.current - 5;
        this.vy = 0.3 * this.h + range(-1, 1);
      }

      draw() {
        this.x += this.vx;
        this.y += this.vy;
        this.rotation += this.rotationSpeed;

        this.opacity += this.dop;
        if (this.opacity > 1) {
          this.opacity = 1;
          this.dop *= -1;
        }
        if (this.opacity < 0 || this.y > this.ymax) this.replace();
        if (!(0 < this.x && this.x < this.xmax)) {
          this.x = (this.x + this.xmax) % this.xmax;
        }

        drawRect(
          ctx,
          ~~this.x,
          ~~this.y,
          this.w,
          this.h,
          this.rotation,
          `${this.rgb},${this.opacity})`
        );
      }
    }

    function range(a: number, b: number) {
      return (b - a) * Math.random() + a;
    }

    function drawRect(
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      w: number,
      h: number,
      rotation: number,
      style: string
    ) {
      ctx.save();
      ctx.translate(x + w / 2, y + h / 2);
      ctx.rotate(rotation);
      ctx.fillStyle = style;
      ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.restore();
    }

    confettiRef.current = Array.from(
      { length: NUM_CONFETTI },
      () => new Confetti()
    );

    const step = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      confettiRef.current.forEach((c) => c.draw());
      requestAnimationFrame(step);
    };
    step();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 999,
        pointerEvents: "none",
      }}
    />
  );
};

export default ConfettiCanvas;
